import { Alert, Switch } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { FieldSection } from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'
import { classNames } from '@/Ship/Helpers/classNames'

import { USER_ROLE_OPTIONS } from '../../Contracts/UserOptions'
import { UserRolesManager } from '../../Stores/UserRolesManager'

import type { UserFormContextLike } from '../../Contracts/IUser'

import styles from '../UserCard.module.css'

/**
 * Вкладка «Права доступу» (легасі `UserAccessSettings.jsx`): сім перемикачів
 * у порядку легасі, кожен зберігається одразу.
 *
 * Видно лише з правом `update_access` — супер адміністратор, і не у власній
 * картці (D-01, С3.3); шаблон картки вкладку без нього не малює.
 * Збережені ролі повертаються у форму картки — шапка одразу показує нові.
 */
export const UserAccessTab = observer(({ formContext }: { formContext: UserFormContextLike }) => {
    const [manager] = useState(() => new UserRolesManager(formContext.values?.roles))
    const uuid = typeof formContext.values?.uuid === 'string' ? formContext.values.uuid : undefined

    const toggle = async (role: string, enabled: boolean): Promise<void> => {
        if (!uuid) return

        const saved = await manager.toggle(uuid, role, enabled)

        if (saved !== null) formContext.setInitialValue?.('roles', saved)
    }

    return (
        <FieldSection title="Ролі">
            <div className={classNames(styles['stack'])}>
                <Hint id="team.user.roles">
                    Зміна ролі зберігається одразу після перемикання. Роль «Адміністратор» уже містить права всіх
                    інших ролей, крім «Супер адміністратор», а «Старший логіст» — права ролі «Логіст».
                </Hint>

                {manager.error ? <Alert type="error" showIcon title={manager.error} /> : null}

                <div className={classNames(styles['roles'])}>
                    {USER_ROLE_OPTIONS.map((option) => (
                        <div key={option.value} className={classNames(styles['roleRow'])}>
                            <span>{option.label}</span>
                            <Switch
                                aria-label={option.label}
                                checked={manager.roles.includes(option.value)}
                                loading={manager.pending === option.value}
                                disabled={manager.pending !== null}
                                onChange={(checked) => void toggle(option.value, checked)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </FieldSection>
    )
})
